import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Linkedin, Phone, MapPin, Loader2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const EMAIL = "owen@arcpointgroup.co.uk";
const LINKEDIN = "https://www.linkedin.com/in/owen-peterson-executive-search";
const PHONE = "+44 7503 410642";
const PHONE_TEL = "+447503410642";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message received. Owen will respond personally.");
    } catch (err) {
      toast.error("Could not send message. Please email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 text-white placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-[#C9A876] focus:ring-1 focus:ring-[#C9A876] transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden bg-[#0E2750] py-24 md:py-36 border-t border-white/5"
    >
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-[#C9A876]/8 blur-[160px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-mono-eyebrow text-[#C9A876]"
            >
              ◆ Founder Contact
            </motion.span>
            <motion.h2
              data-testid="contact-heading"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 font-display text-3xl md:text-4xl lg:text-6xl tracking-tight leading-[0.98] text-white"
            >
              Let's build something{" "}
              <span className="font-serif-accent text-[#C9A876]">exceptional</span>.
            </motion.h2>
            <p className="mt-6 text-slate-400 leading-relaxed text-base md:text-lg max-w-md">
              Whether you're hiring your first commercial leader or your next
              CEO, the conversation starts here — confidentially, founder to founder.
            </p>

            <div className="mt-12 space-y-5">
              <a
                data-testid="contact-email"
                href={`mailto:${EMAIL}`}
                className="group flex items-start gap-4 border border-white/10 hover:border-[#C9A876]/50 px-5 py-5 transition-colors"
              >
                <Mail size={20} strokeWidth={1.5} className="text-[#C9A876] mt-0.5" />
                <div className="flex-1">
                  <div className="font-mono-eyebrow text-slate-400 mb-1">Email</div>
                  <div className="text-white text-sm md:text-base break-all">{EMAIL}</div>
                </div>
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="text-slate-500 group-hover:text-[#C9A876] mt-1"
                />
              </a>
              <a
                data-testid="contact-phone"
                href={`tel:${PHONE_TEL}`}
                className="group flex items-start gap-4 border border-white/10 hover:border-[#C9A876]/50 px-5 py-5 transition-colors"
              >
                <Phone size={20} strokeWidth={1.5} className="text-[#C9A876] mt-0.5" />
                <div className="flex-1">
                  <div className="font-mono-eyebrow text-slate-400 mb-1">Mobile</div>
                  <div className="text-white text-sm md:text-base">{PHONE}</div>
                </div>
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="text-slate-500 group-hover:text-[#C9A876] mt-1"
                />
              </a>
              <a
                data-testid="contact-linkedin"
                href={LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-4 border border-white/10 hover:border-[#C9A876]/50 px-5 py-5 transition-colors"
              >
                <Linkedin size={20} strokeWidth={1.5} className="text-[#C9A876] mt-0.5" />
                <div className="flex-1">
                  <div className="font-mono-eyebrow text-slate-400 mb-1">LinkedIn</div>
                  <div className="text-white text-sm md:text-base">Owen Peterson</div>
                </div>
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="text-slate-500 group-hover:text-[#C9A876] mt-1"
                />
              </a>
              <div className="flex items-start gap-4 px-5 py-5">
                <MapPin size={20} strokeWidth={1.5} className="text-[#C9A876] mt-0.5" />
                <div>
                  <div className="font-mono-eyebrow text-slate-400 mb-1">Location</div>
                  <div className="text-white text-sm md:text-base">United Kingdom · UK-focused executive search</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-white/10 bg-[#15315F]/60 backdrop-blur-sm p-6 md:p-10">
              {sent ? (
                <div data-testid="contact-success" className="py-12 text-center">
                  <div className="font-display text-2xl md:text-3xl text-white mb-3">
                    Thank you.
                  </div>
                  <p className="text-slate-400">Owen will be in touch personally.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="font-mono-eyebrow text-slate-400 mb-2 block">Name *</span>
                      <input
                        data-testid="contact-name"
                        required
                        className={inputCls}
                        value={form.name}
                        onChange={change("name")}
                      />
                    </label>
                    <label className="block">
                      <span className="font-mono-eyebrow text-slate-400 mb-2 block">Email *</span>
                      <input
                        data-testid="contact-email-input"
                        required
                        type="email"
                        className={inputCls}
                        value={form.email}
                        onChange={change("email")}
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="font-mono-eyebrow text-slate-400 mb-2 block">Company</span>
                    <input
                      data-testid="contact-company"
                      className={inputCls}
                      value={form.company}
                      onChange={change("company")}
                    />
                  </label>
                  <label className="block">
                    <span className="font-mono-eyebrow text-slate-400 mb-2 block">Message *</span>
                    <textarea
                      data-testid="contact-message"
                      required
                      rows={5}
                      placeholder="Tell us about the leadership hire you're considering…"
                      className={`${inputCls} resize-none`}
                      value={form.message}
                      onChange={change("message")}
                    />
                  </label>
                  <motion.button
                    whileTap={{ scale: 0.985 }}
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#C9A876] hover:bg-[#DCC298] disabled:opacity-60 text-black text-sm font-semibold tracking-wide transition-colors duration-300"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    {submitting ? "Sending…" : "Send Message"}
                  </motion.button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
